import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ASSUMPTIONS_STORAGE_KEY, DEFAULT_ASSUMPTIONS } from "../lib/assumptions";

const AssumptionsContext = createContext(null);

export function AssumptionsProvider({ children }) {
  const [assumptions, setAssumptions] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_ASSUMPTIONS;
    try {
      const stored = window.localStorage.getItem(ASSUMPTIONS_STORAGE_KEY);
      return stored ? { ...DEFAULT_ASSUMPTIONS, ...JSON.parse(stored) } : DEFAULT_ASSUMPTIONS;
    } catch {
      return DEFAULT_ASSUMPTIONS;
    }
  });

  useEffect(() => {
    window.localStorage.setItem(ASSUMPTIONS_STORAGE_KEY, JSON.stringify(assumptions));
  }, [assumptions]);

  const updateAssumption = useCallback((key, value) => {
    setAssumptions((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetAssumptions = useCallback(() => {
    setAssumptions(DEFAULT_ASSUMPTIONS);
  }, []);

  // Memoise so unchanged assumptions don't re-render every consuming calculator.
  const value = useMemo(
    () => ({ assumptions, updateAssumption, resetAssumptions }),
    [assumptions, updateAssumption, resetAssumptions],
  );

  return <AssumptionsContext.Provider value={value}>{children}</AssumptionsContext.Provider>;
}

export function useAssumptions() {
  const ctx = useContext(AssumptionsContext);
  if (!ctx) throw new Error("useAssumptions must be used within an AssumptionsProvider");
  return ctx;
}
